import { View } from 'backbone.marionette';
import _ from 'lodash';
import Backbone from 'backbone';
import Ladda from 'ladda';
import Radio from 'backbone.radio';

import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import makeFormReadOnly from '@/util/makeFormReadOnly';
import AssessmentFileUploadTmpl from '@/fileupload/AssessmentFileUploadTmpl.hbs';
import AttachmentsCollectionView from '@/views/AttachmentsCollectionView';
import FileUploadModel from '@/fileupload/FileUploadModel';
import ValidFileTypeUtil from '@/util/ValidFileTypeUtil';
import Session from '@/Session';

import SubmitAssessment from '../../services/SubmitAssessment';
import CompleteReturnAssessment from '../../services/CompleteReturnAssessment';

import tmplAssessmentDetailsView from './AssessmentDetailsView.hbs';

const AssessmentDetailsView = View.extend({
  template: tmplAssessmentDetailsView,

  className: 'assessment-details-view',

  ui: {
    assessmentType: '#assessmentType',
    attachments: '.attachments',
    uploadButton: '.btn-upload',
    submitButton: '.btn-submit',
    returnButton: '.btn-return',
    formSubmissionInfo: '.form-submission-info'
  },

  regions: {
    attachments: '@ui.attachments'
  },

  events: {
    'click @ui.uploadButton': 'onClickUploadButton',
    'click @ui.submitButton': 'onClickSubmitButton',
    'change #file-upload-input': 'onFileSelect',
    'click @ui.returnButton': 'onClickReturnButton'
  },

  childEvents: {
    'file:deleted': 'onFileDelete'
  },

  childViewEvents: {
    'file:deleted': 'onFileDelete'
  },

  collectionEvents: {
    'file:deleted': 'onFileDelete'
  },

  initialize() {
    this.model.set('submittedOn', new Date().toISOString().substring(0, 10));

    const attachments = this.model.get('attachments');
    const documentMapping = _.map(attachments, (attachment) => {
      return {
        label: { isHidden: true },
        isReadOnly: !this.model.get('assessmentDetails')?.isFileEditable,
        attachment: {
          isReadOnly: !this.model.get('assessmentDetails')?.isFileEditable,
          attachmentName: attachment?.fileName,
          attachmentId: attachment?.id,
          fileUploadTmpl: AssessmentFileUploadTmpl,
          pathToFileId: 'html.head.script.Envelope.Body.UploadAssessmentAttachmentResponse.AttachmentID.__text',
          metadata: {
            assessmentId: attachment?.objectId,
            attachmentId: attachment?.id,
            fileName: attachment?.fileName
          }
        }
      };
    });

    this.model.set('documentMapping', documentMapping);
  },

  onRender() {
    this.collection = new Backbone.Collection(this.model.get('documentMapping'));
    this.attachmentsCollectionView = new AttachmentsCollectionView({
      collection: this.collection
    });
    this.showChildView('attachments', this.attachmentsCollectionView);

    if (this.model.get('assessmentDetails').isReadOnly) {
      makeFormReadOnly(this.ui.formSubmissionInfo);
      if (this.model.get('assessmentDetails').returnable) {
        this.$el.find('.crs-button').prop('disabled', false);
        this.$el.find('.crs-input').prop('disabled', false);
      }
    }
  },

  onClickUploadButton(e) {
    e.preventDefault();
    this.$('#file-upload-input').trigger('click');
  },

  onFileSelect(e) {
    e.preventDefault();
    const files = e.target.files || e.originalEvent.dataTransfer.files;
    const file = files[0];
    this.uploadFile(file);
    this.trigger('file:uploaded');
  },

  async uploadFile(file) {
    if (!ValidFileTypeUtil(file)) {
      return;
    }

    const assessmentId = this.model.get('assessmentDetails').assessmentId;
    const fileUploadModel = new FileUploadModel({
      file1: file,
      fileName: file.name,
      showError: this.showUploadError,
      metadata: { assessmentId },
      pathToFileId: 'html.head.script.Envelope.Body.UploadAssessmentAttachmentResponse.AttachmentID.__text',
      tmplMethodXML: AssessmentFileUploadTmpl
      // assessmentId
    });

    const spinner = Ladda.create(this.ui.uploadButton[0]);
    spinner.start();

    try {
      await fileUploadModel.upload();

      const uploadedAttachment = {
        label: { isHidden: true },
        isReadOnly: !this.model.get('assessmentDetails')?.isFileEditable,
        attachment: {
          isReadOnly: !this.model.get('assessmentDetails')?.isFileEditable,
          attachmentName: fileUploadModel.get('fileName'),
          attachmentId: fileUploadModel.get('attachmentId'),
          fileUploadTmpl: fileUploadModel.get('tmplMethodXML'),
          pathToFileId: fileUploadModel.get('pathToFileId'),
          validator: ValidFileTypeUtil,
          metadata: fileUploadModel.get('metadata')
        }
      };
      this.attachmentsCollectionView.collection.add(uploadedAttachment);
    } catch (err) {
      if (err && err.responseText.includes('DOCUMENT_EXISTS')) {
        this.showDuplicateFileNameError();
      }
    } finally {
      spinner.stop();
    }
  },

  getAttachmentItemViewData() {},

  showUploadError() {
    showErrorMessage({
      errorCode: 'fileUploadError',
      errorTitle: 'File Upload Error',
      errorMessage: 'Error Uploading File.'
    });
  },

  onFileDelete(modelToDelete) {
    this.attachmentsCollectionView.collection.remove(modelToDelete);
  },

  async onClickSubmitButton(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.submitButton[0]);
    spinner.start();
    this.model.set('submittedOn', new Date().toISOString().substring(0, 10));

    const assessmentId = this.model.get('assessmentDetails').assessmentId;

    const SubmitService = EXTERNAL ? SubmitAssessment : CompleteReturnAssessment;
    try {
      if (EXTERNAL) {
        await SubmitService(assessmentId, Session.user.actingAsCommandId);
        this.showSubmissionSuccess();
        Radio.channel('navigate').request('show:ext:dashboard');
      } else {
        await SubmitService(assessmentId, '5');
        this.showSubmissionSuccess();
        Radio.channel('navigate').request('show:int:dashboard');
      }
    } catch (err) {
      if (err) {
        this.showSubmissionError();
      }
    } finally {
      spinner.stop();
    }
  },

  showSubmissionSuccess() {
    showSuccessMessage({
      code: 'submissionSuccess',
      title: 'Submission Received',
      message: 'Your assessment has been successfully submitted.',
      hideAfter: 5
    });
  },

  showSubmissionError() {
    showErrorMessage({
      errorCode: 'submissionError',
      errorTitle: 'Submission Error',
      errorMessage: 'An error occurred while submitting assessment. Try again or contact an administrator if your issue persists.'
    });
  },

  async onClickReturnButton(e) {
    e.preventDefault();
    const assessmentId = this.model.get('assessmentDetails').assessmentId;
    try {
      await CompleteReturnAssessment(assessmentId, '4');
      this.showReturnSuccess();
      Radio.channel('navigate').request('show:int:dashboard');
    } catch (err) {
      if (err) {
        this.showReturnError();
      }
    }
  },

  showReturnSuccess() {
    showSuccessMessage({
      code: 'returnSuccess',
      title: 'Assessment Returned Successfully',
      message: 'This assessment has been successfully returned to customer.',
      hideAfter: 5
    });
  },

  showReturnError() {
    showErrorMessage({
      errorCode: 'returnError',
      errorTitle: 'Return Error',
      errorMessage:
        'An error occurred while returning assessment. Try again or contact an administrator if your issue persists.'
    });
  },

  showDuplicateFileNameError() {
    showErrorMessage({
      errorCode: 'documentExists',
      errorTitle: 'Duplicate File Name',
      errorMessage:
        'Every document uploaded to an assessment must have a unique file name. Please update the file name and try again.'
    });
  }
});

export default AssessmentDetailsView;
